import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputLabel,
  Menu, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon     from '@mui/icons-material/ArrowBack'
import PersonAddIcon     from '@mui/icons-material/PersonAdd'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon  from '@mui/icons-material/EditOutlined'
import StarIcon          from '@mui/icons-material/Star'
import StarOutlineIcon   from '@mui/icons-material/StarBorder'
import SwapHorizIcon     from '@mui/icons-material/SwapHoriz'
import VisibilityIcon    from '@mui/icons-material/Visibility'
import MoreVertIcon      from '@mui/icons-material/MoreVert'
import MailOutlineIcon   from '@mui/icons-material/EmailOutlined'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase }  from '../../../../supabaseClient'
import { useAuth }   from '../../../../auth/AuthContext'
import { Class, ClassStatus, EnrollmentStatus } from '../../../../types'
import ClassScheduleSection from '../../../../components/classes/ClassScheduleSection'
import ClassOfficeHoursSection from '../../../../components/classes/ClassOfficeHoursSection'
import ClassCommunicationsSection, { CommTemplate } from '../../../../components/classes/ClassCommunicationsSection'
import ChangeClassDialog from '../../../../components/students/ChangeClassDialog'
import { ParentSignupChip, loadParentSignup, ParentSignup } from '../../../../components/students/parentSignup'

// ── Types ─────────────────────────────────────────────────────────────────────

interface StudentRow {
  enrollment_id: string
  student_id:    string
  user_id:       string          // the student's login user (invite-parent input)
  project_id:    string | null
  current_step:  number | null
  enrolled_at:   string
  status:        EnrollmentStatus
  first_name:    string
  last_name:     string
  email:         string
  grade:         number
  parent_signup: ParentSignup
}

interface ClassMentorRow {
  id:         string   // class_mentor.id
  user_id:    string
  role:       string   // 'lead' (primary) | 'mentor' | 'ta' | 'domain_specialist'
  first_name: string
  last_name:  string
  email:      string
}

interface UserResult {
  user_id:    string
  first_name: string
  last_name:  string
  email:      string
  role:       string
}

interface EditClassForm {
  name:          string
  academic_year: string
  start_date:    string
  end_date:      string
  capacity:      string
  status:        ClassStatus
}

const EMPTY_EDIT_FORM: EditClassForm = {
  name: '', academic_year: '', start_date: '', end_date: '', capacity: '', status: 'upcoming',
}

const CLASS_STATUSES: ClassStatus[] = ['upcoming', 'active', 'completed', 'archived']

const ENROLLMENT_STATUS_COLOR: Record<EnrollmentStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  active:      'success',
  withdrawn:   'error',
  on_hold:     'warning',
  completed:   'default',
  transferred: 'info',
}

const STEP_LABELS: Record<number, string> = {
  1: 'Topic', 2: 'Question', 3: 'Background', 4: 'Hypothesis',
  5: 'Design', 6: 'Data', 7: 'Analysis', 8: 'Report',
}

function fmtDate(s: string) {
  if (!s) return '—'
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ClassDetailPage() {
  const { classId } = useParams<{ classId: string }>()
  const { session, role }  = useAuth()
  const navigate     = useNavigate()

  const [cls,            setCls]            = useState<Class | null>(null)
  const [students,       setStudents]       = useState<StudentRow[]>([])
  const [classMentors,   setClassMentors]   = useState<ClassMentorRow[]>([])
  const [loading,        setLoading]        = useState(true)

  // Class Mentors dialog
  const [mentorOpen,       setMentorOpen]       = useState(false)
  const [mentorSearch,     setMentorSearch]     = useState('')
  const [allMentors,       setAllMentors]       = useState<UserResult[]>([])
  const [mentorResults,    setMentorResults]    = useState<UserResult[]>([])
  const [mentorLoadingAll, setMentorLoadingAll] = useState(false)
  const [addingMentor,     setAddingMentor]     = useState(false)
  const [mentorErr,        setMentorErr]        = useState<string | null>(null)
  const [removingMentor,   setRemovingMentor]   = useState<string | null>(null)
  const [settingPrimary,   setSettingPrimary]   = useState<string | null>(null)

  // Student enroll dialog
  const [studentOpen,    setStudentOpen]    = useState(false)
  const [studentSearch,  setStudentSearch]  = useState('')
  const [studentResults, setStudentResults] = useState<UserResult[]>([])
  const [searching,      setSearching]      = useState(false)
  const [enrolling,      setEnrolling]      = useState(false)

  // Transfer student dialog
  const [transferStudent, setTransferStudent] = useState<StudentRow | null>(null)

  // Roster row actions menu
  const [rosterMenuAnchor, setRosterMenuAnchor] = useState<null | HTMLElement>(null)
  const [rosterMenuRow,    setRosterMenuRow]    = useState<StudentRow | null>(null)

  // Bulk parent invites
  const [inviteOpen,   setInviteOpen]   = useState(false)
  const [inviting,     setInviting]     = useState(false)
  const [inviteResult, setInviteResult] = useState<string | null>(null)

  // Class message
  const [msgOpen,     setMsgOpen]     = useState(false)
  const [msgSubject,  setMsgSubject]  = useState('')
  const [msgBody,     setMsgBody]     = useState('')
  const [msgCcParents, setMsgCcParents] = useState(false)
  const [msgSending,  setMsgSending]  = useState(false)
  const [msgResult,   setMsgResult]   = useState<string | null>(null)

  async function sendClassMessage() {
    if (!classId || !msgSubject.trim() || !msgBody.trim()) return
    setMsgSending(true)
    const { data, error: invokeErr } = await supabase.functions.invoke('send-class-message', {
      body: { class_id: classId, subject: msgSubject.trim(), body: msgBody.trim(), cc_parents: msgCcParents },
    })
    setMsgSending(false)
    if (invokeErr || data?.error) { setMsgResult(data?.error ?? 'Could not send. Please try again.'); return }
    setMsgResult(`Message sent to ${data?.sent ?? 0} student${data?.sent === 1 ? '' : 's'}${msgCcParents ? ' (parents CC\'d)' : ''}. A copy was forwarded to care@ardentacademy.com.`)
    setMsgOpen(false); setMsgSubject(''); setMsgBody(''); setMsgCcParents(false)
  }

  // Edit class dialog
  const [editOpen,   setEditOpen]   = useState(false)
  const [editForm,   setEditForm]   = useState<EditClassForm>(EMPTY_EDIT_FORM)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editErr,    setEditErr]    = useState<string | null>(null)

  // ── Load ────────────────────────────────────────────────────────────────────

  async function load() {
    if (!classId) return

    const [{ data: clsData }, { data: enrollments }, { data: mentorRowsRaw }] = await Promise.all([
      supabase.from('class').select('*').eq('class_id', classId).maybeSingle(),
      supabase.from('class_enrollment').select('enrollment_id, student_id, enrolled_at, status').eq('class_id', classId).eq('status', 'active'),
      supabase.from('class_mentor').select('id, user_id, role, created_at').eq('class_id', classId),
    ])

    setCls(clsData)

    const enrollmentRows = enrollments ?? []
    const mentorRows     = mentorRowsRaw ?? []

    // Resolve student profiles + users
    let builtStudents: StudentRow[] = []
    if (enrollmentRows.length > 0) {
      const studentIds = enrollmentRows.map(e => e.student_id)

      const [{ data: profiles }, { data: projects }] = await Promise.all([
        supabase.from('student_profile').select('student_id, grade, user_id').in('student_id', studentIds),
        supabase.from('project').select('project_id, student_id, current_step').in('student_id', studentIds).eq('status', 'active'),
      ])

      const profileMap: Record<string, { grade: number; user_id: string }> =
        Object.fromEntries((profiles ?? []).map(p => [p.student_id, p]))
      const projectMap: Record<string, { project_id: string; current_step: number }> =
        Object.fromEntries((projects ?? []).map(p => [p.student_id, { project_id: p.project_id, current_step: p.current_step }]))

      const userIds = (profiles ?? []).map(p => p.user_id)
      const [{ data: userRows }, parentSignupByStudent] = await Promise.all([
        supabase.from('users').select('user_id, first_name, last_name, email').in('user_id', userIds),
        loadParentSignup(studentIds),
      ])
      const userMap: Record<string, { first_name: string; last_name: string; email: string }> =
        Object.fromEntries((userRows ?? []).map(u => [u.user_id, u]))

      builtStudents = enrollmentRows.map(e => {
        const profile = profileMap[e.student_id]
        const user    = profile ? userMap[profile.user_id] : undefined
        const proj = projectMap[e.student_id]
        return {
          enrollment_id: e.enrollment_id,
          student_id:    e.student_id,
          user_id:       profile?.user_id ?? '',
          project_id:    proj?.project_id ?? null,
          current_step:  proj?.current_step ?? null,
          enrolled_at:   e.enrolled_at,
          status:        e.status,
          first_name:    user?.first_name ?? '—',
          last_name:     user?.last_name  ?? '',
          email:         user?.email      ?? '—',
          grade:         profile?.grade   ?? 0,
          parent_signup: parentSignupByStudent[e.student_id] ?? 'none',
        }
      })
    }
    // Sort roster by grade ascending (then by name for stable ordering).
    builtStudents.sort((a, b) =>
      a.grade - b.grade ||
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    )
    setStudents(builtStudents)

    // Resolve class mentor user names
    if (mentorRows.length > 0) {
      const mentorUserIds = mentorRows.map(m => m.user_id)
      const { data: mentorUsers } = await supabase
        .from('users').select('user_id, first_name, last_name, email').in('user_id', mentorUserIds)
      const mentorUserMap: Record<string, { first_name: string; last_name: string; email: string }> =
        Object.fromEntries((mentorUsers ?? []).map(u => [u.user_id, u]))

      setClassMentors(
        mentorRows
          .map(m => ({
            id:         m.id,
            user_id:    m.user_id,
            role:       m.role,
            first_name: mentorUserMap[m.user_id]?.first_name ?? '—',
            last_name:  mentorUserMap[m.user_id]?.last_name  ?? '',
            email:      mentorUserMap[m.user_id]?.email      ?? '—',
          }))
          // Primary (lead) first, then by name.
          .sort((a, b) =>
            (a.role === 'lead' ? 0 : 1) - (b.role === 'lead' ? 0 : 1) ||
            `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
          )
      )
    } else {
      setClassMentors([])
    }

    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [classId])

  // ── Class Mentors management ─────────────────────────────────────────────────

  async function loadAllMentors() {
    setMentorLoadingAll(true)
    const { data } = await supabase
      .from('users')
      .select('user_id, first_name, last_name, email, role')
      .eq('role', 'mentor')
      .order('first_name')
    const list = data ?? []
    setAllMentors(list)
    setMentorResults(list)
    setMentorLoadingAll(false)
  }

  function handleMentorSearch(q: string) {
    setMentorSearch(q)
    if (!q.trim()) { setMentorResults(allMentors); return }
    const lower = q.toLowerCase()
    setMentorResults(
      allMentors.filter(u =>
        u.email.toLowerCase().includes(lower) ||
        u.first_name.toLowerCase().includes(lower) ||
        u.last_name.toLowerCase().includes(lower)
      )
    )
  }

  async function addClassMentor(userId: string, notifyEmail?: string, notifyName?: string) {
    if (!classId) return
    setAddingMentor(true)
    setMentorErr(null)
    // First mentor on a class becomes the primary (lead); the rest are helpers.
    const role = classMentors.some(m => m.role === 'lead') ? 'mentor' : 'lead'
    const { error } = await supabase.from('class_mentor').upsert(
      { class_id: classId, user_id: userId, role },
      { onConflict: 'class_id,user_id' }
    )
    if (error) { setMentorErr(error.message); setAddingMentor(false); return }
    // Notify existing mentor of class assignment (fire-and-forget)
    if (notifyEmail && notifyName && cls) {
      supabase.functions.invoke('notify-mentor-class', {
        body: {
          to_email:    notifyEmail,
          mentor_name: notifyName,
          class_name:  cls.name,
          app_url:     window.location.origin,
        },
      }).catch(() => {})
    }
    setMentorOpen(false); setMentorSearch(''); setMentorResults([])
    setAddingMentor(false)
    load()
  }

  async function removeClassMentor(mentorId: string) {
    setRemovingMentor(mentorId)
    await supabase.from('class_mentor').delete().eq('id', mentorId)
    setClassMentors(prev => prev.filter(m => m.id !== mentorId))
    setRemovingMentor(null)
  }

  async function makePrimary(mentor: ClassMentorRow) {
    if (mentor.role === 'lead') return
    setMentorErr(null)
    setSettingPrimary(mentor.id)
    // Demote the current primary first so the one-lead-per-class index never
    // sees two leads at once.
    const currentLead = classMentors.find(m => m.role === 'lead')
    if (currentLead) {
      const { error } = await supabase.from('class_mentor').update({ role: 'mentor' }).eq('id', currentLead.id)
      if (error) { setMentorErr(error.message); setSettingPrimary(null); return }
    }
    const { error } = await supabase.from('class_mentor').update({ role: 'lead' }).eq('id', mentor.id)
    setSettingPrimary(null)
    if (error) { setMentorErr(error.message); return }
    load()
  }

  // ── Student enroll ───────────────────────────────────────────────────────────

  async function handleStudentSearch(q: string) {
    setStudentSearch(q)
    if (q.length < 2) { setStudentResults([]); return }
    setSearching(true)
    const { data } = await supabase
      .from('student_profile').select('student_id, users!inner(first_name, last_name, email)')
      .ilike('users.email', `%${q}%`).limit(10)
    setStudentResults((data ?? []).map((r: any) => ({
      user_id: r.student_id, first_name: r.users.first_name, last_name: r.users.last_name, email: r.users.email, role: 'student',
    })))
    setSearching(false)
  }

  async function enrollStudent(studentId: string) {
    if (!classId || !session) return
    setEnrolling(true)
    await supabase.from('class_enrollment').upsert(
      { class_id: classId, student_id: studentId, enrolled_by: session.user.id, status: 'active' },
      { onConflict: 'class_id,student_id' }
    )
    setStudentOpen(false); setStudentSearch(''); setStudentResults([])
    setEnrolling(false)
    load()
  }

  // ── Edit class ───────────────────────────────────────────────────────────────

  function openEdit() {
    if (!cls) return
    setEditErr(null)
    setEditForm({
      name:          cls.name,
      academic_year: cls.academic_year,
      start_date:    cls.start_date,
      end_date:      cls.end_date,
      capacity:      String(cls.capacity),
      status:        cls.status,
    })
    setEditOpen(true)
  }

  async function handleSaveEdit() {
    if (!classId) return
    setEditErr(null)

    if (!editForm.name.trim())   { setEditErr('Name is required.'); return }
    if (!editForm.start_date || !editForm.end_date) { setEditErr('Start and end dates are required.'); return }
    if (editForm.end_date < editForm.start_date)    { setEditErr('End date must be on or after the start date.'); return }
    const capacityNum = Number(editForm.capacity)
    if (!Number.isInteger(capacityNum) || capacityNum < 1) { setEditErr('Capacity must be a positive whole number.'); return }
    if (capacityNum < students.length) {
      setEditErr(`Capacity can't be below the ${students.length} students already enrolled.`); return
    }

    setSavingEdit(true)
    const { error } = await supabase.from('class').update({
      name:          editForm.name.trim(),
      academic_year: editForm.academic_year.trim(),
      start_date:    editForm.start_date,
      end_date:      editForm.end_date,
      capacity:      capacityNum,
      status:        editForm.status,
    }).eq('class_id', classId)
    setSavingEdit(false)
    if (error) { setEditErr(error.message); return }
    setEditOpen(false)
    load()
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  // Admin/OM manage any class; a class's own mentors fully manage that class.
  const canManageClass =
    role === 'admin' || role === 'office_manager' ||
    (!!session && classMentors.some(m => m.user_id === session.user.id))

  // Current step is a mentor-view detail (not shown to office managers);
  // parent signup is the admin/office-manager column.
  const showStep   = role === 'admin' || role === 'instructor' || role === 'mentor'
  const showParent = role === 'admin' || role === 'office_manager'

  // Parents who haven't been invited yet (skip already-invited and signed-up).
  const invitableParents = students.filter(s => s.parent_signup === 'not_invited' && s.user_id)

  async function handleInviteParents() {
    setInviting(true); setInviteResult(null)
    let sent = 0, failed = 0
    for (const s of invitableParents) {
      try {
        const { data, error } = await supabase.functions.invoke('invite-parent', {
          body: { student_user_id: s.user_id },
        })
        if (!error && data?.ok === true) sent += 1
        else failed += 1
      } catch { failed += 1 }
    }
    setInviting(false)
    setInviteOpen(false)
    setInviteResult(`Invited ${sent} parent${sent === 1 ? '' : 's'}${failed ? `, ${failed} could not be sent` : ''}.`)
    await load()
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (!cls)    return <Box sx={{ p: 4 }}><Typography>Class not found.</Typography></Box>

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1000 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <IconButton onClick={() => navigate('/classes')} size="small"><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{cls.name}</Typography>
        <Chip label={cls.status} size="small" sx={{ ml: 0.5 }} />
        {canManageClass && (
          <Button size="small" startIcon={<EditOutlinedIcon />} onClick={openEdit} sx={{ ml: 'auto' }}>
            Edit class
          </Button>
        )}
      </Box>
      <Typography color="text.secondary" sx={{ mb: 4, ml: 5 }}>
        {cls.academic_year} · {fmtDate(cls.start_date)} – {fmtDate(cls.end_date)} · Capacity: {cls.capacity}
      </Typography>

      {/* ── Class Mentors ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Class Mentors ({classMentors.length})</Typography>
        {canManageClass && (
          <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => { setMentorOpen(true); setMentorSearch(''); setMentorErr(null); loadAllMentors() }}>
            Add Mentor
          </Button>
        )}
      </Box>
      <Paper variant="outlined" sx={{ mb: 4 }}>
        {classMentors.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">No mentors on this class yet.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {classMentors.map(m => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{m.first_name} {m.last_name}</Typography>
                      {m.role === 'lead'
                        ? <Chip label="Primary" color="primary" size="small" icon={<StarIcon />} />
                        : <Chip label="Mentor" variant="outlined" size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{m.email}</Typography></TableCell>
                  <TableCell align="right">
                    {canManageClass && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        {m.role !== 'lead' && (
                          <Button
                            size="small"
                            startIcon={settingPrimary === m.id ? <CircularProgress size={14} /> : <StarOutlineIcon />}
                            disabled={settingPrimary !== null}
                            onClick={() => makePrimary(m)}
                          >
                            Make primary
                          </Button>
                        )}
                        <IconButton size="small" color="error" disabled={removingMentor === m.id} onClick={() => removeClassMentor(m.id)}>
                          {removingMentor === m.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Divider sx={{ mb: 4 }} />

      {/* ── Class Schedule ── */}
      <ClassScheduleSection
        classId={classId!}
        mentors={classMentors.map(m => ({ user_id: m.user_id, first_name: m.first_name, last_name: m.last_name }))}
        editable={canManageClass}
        canMarkAttendance={role === 'admin' || role === 'office_manager' || role === 'instructor' || role === 'mentor'}
        onMessageClass={canManageClass && students.length > 0 ? () => { setMsgResult(null); setMsgOpen(true) } : undefined}
      />

      <Divider sx={{ my: 4 }} />

      {/* ── Office Hours ── */}
      <ClassOfficeHoursSection
        mentors={classMentors.map(m => ({ user_id: m.user_id, first_name: m.first_name, last_name: m.last_name, role: m.role }))}
        editable={canManageClass}
      />

      <Divider sx={{ my: 4 }} />

      {/* ── Communications ──
          Templates for the messages that recur. Picking one fills the same
          compose dialog the "Message class" button opens, so the send path
          and its confirmation are unchanged. */}
      <ClassCommunicationsSection
        classId={classId!}
        editable={canManageClass && students.length > 0}
        onUse={(t: CommTemplate) => {
          setMsgResult(null)
          setMsgSubject(t.subject)
          setMsgBody(t.body)
          setMsgOpen(true)
        }}
      />

      <Divider sx={{ my: 4 }} />

      {/* ── Roster ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Roster ({students.length} / {cls.capacity})</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showParent && (
            <Button
              variant="outlined" size="small" startIcon={<MailOutlineIcon />}
              onClick={() => { setInviteResult(null); setInviteOpen(true) }}
              disabled={invitableParents.length === 0}
            >
              Invite parents{invitableParents.length > 0 ? ` (${invitableParents.length})` : ''}
            </Button>
          )}
          {canManageClass && (
            <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={() => setStudentOpen(true)}>
              Add Student
            </Button>
          )}
        </Box>
      </Box>
      {inviteResult && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setInviteResult(null)}>{inviteResult}</Alert>}
      {msgResult && <Alert severity={msgResult.startsWith('Could not') ? 'error' : 'success'} sx={{ mb: 1.5 }} onClose={() => setMsgResult(null)}>{msgResult}</Alert>}
      <Paper variant="outlined">
        {students.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">No students enrolled yet.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                {showStep && <TableCell sx={{ fontWeight: 600 }}>Current step</TableCell>}
                {showParent && <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Enrolled</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map(s => (
                <TableRow key={s.enrollment_id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{s.first_name} {s.last_name}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{s.email}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{s.grade}</Typography></TableCell>
                  {showStep && (
                    <TableCell>
                      {s.current_step
                        ? <Typography variant="body2">Step {s.current_step} · {STEP_LABELS[s.current_step] ?? ''}</Typography>
                        : <Typography variant="body2" color="text.secondary">No project</Typography>}
                    </TableCell>
                  )}
                  {showParent && (
                    <TableCell><ParentSignupChip value={s.parent_signup} /></TableCell>
                  )}
                  <TableCell><Typography variant="caption">{new Date(s.enrolled_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography></TableCell>
                  <TableCell><Chip label={s.status} color={ENROLLMENT_STATUS_COLOR[s.status]} size="small" /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={e => { setRosterMenuAnchor(e.currentTarget); setRosterMenuRow(s) }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Add Class Mentor dialog ── */}
      <Dialog open={mentorOpen} onClose={() => { setMentorOpen(false); setMentorSearch(''); setMentorResults([]); setAllMentors([]); setMentorErr(null) }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Mentor to Class</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {mentorErr && <Alert severity="error" sx={{ mb: 2 }}>{mentorErr}</Alert>}
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField label="Filter by name or email" value={mentorSearch} onChange={e => handleMentorSearch(e.target.value)} fullWidth size="small" autoFocus />
          </Box>
          {mentorLoadingAll ? (
            <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
          ) : mentorResults.length === 0 ? (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {allMentors.length === 0
                  ? 'No mentors in the system yet. Add one from the Staff page first.'
                  : `No mentor matches "${mentorSearch}".`}
              </Typography>
            </Box>
          ) : (
            mentorResults.map(u => (
              <Box key={u.user_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                </Box>
                <Button size="small" variant="contained" disabled={addingMentor}
                  onClick={() => addClassMentor(u.user_id, u.email, `${u.first_name} ${u.last_name}`)}>
                  Add
                </Button>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMentorOpen(false); setMentorSearch(''); setMentorResults([]); setAllMentors([]); setMentorErr(null) }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add student dialog ── */}
      <Dialog open={studentOpen} onClose={() => { setStudentOpen(false); setStudentSearch(''); setStudentResults([]) }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Student</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField label="Search by email" value={studentSearch} onChange={e => handleStudentSearch(e.target.value)} fullWidth size="small" sx={{ mt: 1 }} autoFocus />
          {searching && <Box sx={{ mt: 2, textAlign: 'center' }}><CircularProgress size={22} /></Box>}
          {studentResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {studentResults.map(r => (
                <Box key={r.user_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.first_name} {r.last_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  </Box>
                  <Button size="small" variant="contained" disabled={enrolling} onClick={() => enrollStudent(r.user_id)}>Enroll</Button>
                </Box>
              ))}
            </Box>
          )}
          {studentSearch.length >= 2 && !searching && studentResults.length === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">No students found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStudentOpen(false); setStudentSearch(''); setStudentResults([]) }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Transfer student dialog ── */}
      {/* Roster row actions */}
      <Menu
        anchorEl={rosterMenuAnchor}
        open={Boolean(rosterMenuAnchor)}
        onClose={() => { setRosterMenuAnchor(null); setRosterMenuRow(null) }}
      >
        <MenuItem
          onClick={() => {
            if (rosterMenuRow) window.open(`/view-as/${rosterMenuRow.student_id}/step/${rosterMenuRow.current_step ?? 1}`, '_blank')
            setRosterMenuAnchor(null)
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} /> View student's work
        </MenuItem>
        {canManageClass && (
          <MenuItem onClick={() => { setTransferStudent(rosterMenuRow); setRosterMenuAnchor(null) }}>
            <SwapHorizIcon fontSize="small" sx={{ mr: 1.5 }} /> Transfer
          </MenuItem>
        )}
      </Menu>

      <ChangeClassDialog
        open={!!transferStudent}
        studentId={transferStudent?.student_id ?? null}
        studentLabel={transferStudent ? `${transferStudent.first_name} ${transferStudent.last_name} (${transferStudent.email})` : ''}
        currentClassId={classId ?? null}
        currentClassName={cls.name}
        onClose={() => setTransferStudent(null)}
        onMoved={() => { setTransferStudent(null); load() }}
      />

      {/* ── Invite parents confirm ── */}
      <Dialog open={inviteOpen} onClose={inviting ? undefined : () => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Invite parents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will email a portal invitation to <strong>{invitableParents.length} parent{invitableParents.length === 1 ? '' : 's'}</strong> who
            haven&apos;t been invited yet. Already-invited and signed-up parents are skipped.
          </Typography>
          <Box sx={{ maxHeight: 160, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
            {invitableParents.map(s => (
              <Typography key={s.student_id} variant="body2" color="text.secondary">
                • {s.first_name} {s.last_name}
              </Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The invitation is sent from you — parents can reply to your email.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteParents} disabled={inviting || invitableParents.length === 0}>
            {inviting ? <CircularProgress size={20} /> : `Send ${invitableParents.length} invite${invitableParents.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Message class ── */}
      <Dialog open={msgOpen} onClose={msgSending ? undefined : () => setMsgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Message class</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Emails all <strong>{students.length}</strong> student{students.length === 1 ? '' : 's'} in {cls.name}.
            A copy of each goes to care@ardentacademy.com, and Jolin gets a one-line
            summary. Students can reply to your email.
          </Typography>
          <TextField
            label="Subject" size="small" fullWidth required sx={{ mb: 2 }}
            value={msgSubject} onChange={e => setMsgSubject(e.target.value)} disabled={msgSending}
          />
          <TextField
            label="Message" size="small" fullWidth required multiline rows={8}
            value={msgBody} onChange={e => setMsgBody(e.target.value)} disabled={msgSending}
            helperText="[Student] is replaced with each student's first name."
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={msgCcParents} onChange={e => setMsgCcParents(e.target.checked)} disabled={msgSending} />}
            label={<Typography variant="body2">CC parents</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMsgOpen(false)} disabled={msgSending}>Cancel</Button>
          <Button
            variant="contained" onClick={sendClassMessage}
            disabled={msgSending || !msgSubject.trim() || !msgBody.trim() || students.length === 0}
          >
            {msgSending ? <CircularProgress size={20} /> : `Send to ${students.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit class dialog ── */}
      <Dialog open={editOpen} onClose={savingEdit ? undefined : () => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Class</DialogTitle>
        <DialogContent>
          {editErr && <Alert severity="error" sx={{ mb: 2 }}>{editErr}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name" size="small" fullWidth required
              value={editForm.name}
              onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Academic year" size="small"
                value={editForm.academic_year}
                onChange={e => setEditForm(f => ({ ...f, academic_year: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Capacity" size="small" type="number"
                value={editForm.capacity}
                onChange={e => setEditForm(f => ({ ...f, capacity: e.target.value }))}
                slotProps={{ htmlInput: { min: 1 } }}
                sx={{ width: 130 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start date" size="small" type="date" required
                value={editForm.start_date}
                onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="End date" size="small" type="date" required
                value={editForm.end_date}
                onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status" value={editForm.status}
                onChange={e => setEditForm(f => ({ ...f, status: e.target.value as ClassStatus }))}
              >
                {CLASS_STATUSES.map(s => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={savingEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
